import type { ScenarioConfig } from '../../engine/plant/types';

const H = 3600;

export const scenarios: ScenarioConfig[] = [
  {
    id: 'steady',
    name: 'Steady shift',
    category: 'sandbox',
    brief:
      'Design conditions, everything in automatic. The reactor sits at 350 K with 95 % ' +
      'conversion and the jacket taking 127 kW. Use it to learn the unit and to run step ' +
      'tests before you meet it in anger.',
    durationS: 8 * H,
    events: [],
    objectives: [
      { id: 'temp', text: 'Hold the reactor between 340 K and 358 K', kind: 'maintain', condition: { op: 'between', tag: 'TT-201.PV', lo: 340, hi: 358 }, toleranceS: 60, weight: 3 },
      { id: 'conv', text: 'Keep unreacted A below 0.25 kmol/m³', kind: 'maintain', condition: { op: '<', tag: 'AT-201.PV', value: 0.25 }, toleranceS: 120, weight: 2 },
      { id: 'notrip', text: 'No trip', kind: 'avoid', condition: { op: 'tripped', id: 'I-201' }, weight: 3 },
    ],
    stars: [55, 78, 93],
    tip: 'Watch the jacket outlet temperature as well as the reactor. It tells you how much cooling margin you have left.',
  },

  {
    id: 'summer',
    name: 'Summer cooling water',
    category: 'campaign',
    brief:
      'The cooling-water supply is climbing — 9 K over the next two hours as the day heats ' +
      'up. The jacket driving force falls with it. The unit will hold, but only if you notice ' +
      'the margin disappearing before the temperature does.',
    durationS: 6 * H,
    events: [
      { id: 'warm', at: 20 * 60, effects: [{ do: 'fault', id: 'CW-SUMMER', on: true }], announce: 'Cooling-water supply temperature rising.', hint: 'Jacket flow will rise to compensate. When FIC-202 runs out of valve, your only remaining levers are feed rate and feed ratio.' },
    ],
    objectives: [
      { id: 'temp', text: 'Hold the reactor between 340 K and 358 K', kind: 'maintain', condition: { op: 'between', tag: 'TT-201.PV', lo: 340, hi: 358 }, toleranceS: 300, weight: 4 },
      { id: 'notrip', text: 'No trip', kind: 'avoid', condition: { op: 'tripped', id: 'I-201' }, weight: 4 },
      { id: 'rate', text: 'Keep making product — at least 6 m³/h of feed A', kind: 'maintain', condition: { op: '>', tag: 'FT-101.PV', value: 6 }, toleranceS: 900, weight: 2 },
    ],
    stars: [50, 74, 90],
    tip: 'Cutting the feed rate is the cheapest way to buy cooling margin. Cutting it too far throws away the shift’s production.',
  },

  {
    id: 'cw-trip',
    name: 'Cooling-water pump trip',
    category: 'campaign',
    brief:
      'The worst thing that can happen to an exothermic reactor is losing its heat sink. When ' +
      'it happens you have a few minutes, not a few hours. Know what you are going to do ' +
      'before it starts.',
    durationS: 3 * H,
    events: [
      { id: 'trip', at: 25 * 60, effects: [{ do: 'fault', id: 'CWP-TRIP', on: true }], announce: 'Cooling-water pump has tripped.', hint: 'Cut the feed. Everything else is secondary — with no jacket flow the reactor has no way to lose the heat it is still making.' },
    ],
    objectives: [
      { id: 'notrip', text: 'Keep the reactor below 380 K — no high-high trip', kind: 'avoid', condition: { op: 'tripped', id: 'I-201' }, weight: 6 },
      { id: 'noquench', text: 'Do not need the emergency quench', kind: 'avoid', condition: { op: '>', tag: 'ZT-901.PV', value: 5 }, weight: 3 },
      { id: 'safe', text: 'Bring the reactor back below 355 K', kind: 'achieve', condition: { op: '<', tag: 'TT-201.PV', value: 355 }, weight: 3 },
    ],
    stars: [45, 72, 92],
    tip: 'Heat generation falls with the reactant left in the vessel. Cutting the feed starves the reaction; it is the fastest brake you have.',
  },

  {
    id: 'feed-step',
    name: 'A stronger batch of feed',
    category: 'campaign',
    brief:
      'A new consignment of feed A is on line and it is stronger than the last. More reactant ' +
      'at the same flow means more heat, and the composition analyzer has three minutes of ' +
      'sample dead time, so the temperature will tell you first.',
    durationS: 4 * H,
    events: [
      { id: 'step', at: 30 * 60, effects: [{ do: 'fault', id: 'FEED-A-STEP', on: true }], hint: 'The feed concentration analyzer AT-101 is three minutes behind. Trust the reactor temperature.' },
    ],
    objectives: [
      { id: 'temp', text: 'Hold the reactor between 340 K and 358 K', kind: 'maintain', condition: { op: 'between', tag: 'TT-201.PV', lo: 340, hi: 358 }, toleranceS: 240, weight: 4 },
      { id: 'notrip', text: 'No trip', kind: 'avoid', condition: { op: 'tripped', id: 'I-201' }, weight: 3 },
      { id: 'conv', text: 'Keep unreacted A below 0.25 kmol/m³', kind: 'maintain', condition: { op: '<', tag: 'AT-201.PV', value: 0.25 }, toleranceS: 600, weight: 2 },
    ],
    stars: [50, 74, 90],
    tip: 'A feed-ratio change dilutes the reactor without cutting throughput of solvent. It is a gentler tool than the feed rate.',
  },

  {
    id: 'agitator',
    name: 'Agitator failure',
    category: 'campaign',
    brief:
      'The agitator is about to fail. Jacket heat transfer will fade to about a third of ' +
      'normal over the next few minutes — slowly enough that the temperature drifts rather ' +
      'than jumps, which is exactly what makes it easy to miss.',
    durationS: 3 * H,
    events: [
      { id: 'fail', at: 20 * 60, effects: [{ do: 'fault', id: 'AGITATOR-FAIL', on: true }], hint: 'Watch the jacket UA value and the jacket flow. The controller will open the valve further and further for the same temperature — that is the tell.' },
    ],
    objectives: [
      { id: 'notrip', text: 'No high-high temperature trip', kind: 'avoid', condition: { op: 'tripped', id: 'I-201' }, weight: 5 },
      { id: 'temp', text: 'Keep the reactor below 365 K', kind: 'maintain', condition: { op: '<', tag: 'TT-201.PV', value: 365 }, toleranceS: 300, weight: 3 },
      { id: 'safe', text: 'End the shift with the reactor below 355 K', kind: 'achieve', condition: { op: '<', tag: 'TT-201.PV', value: 355 }, weight: 2 },
    ],
    stars: [50, 74, 90],
    tip: 'A control valve that keeps opening while the measurement holds still means the process gain has changed. Find out why.',
  },

  {
    id: 'catalyst',
    name: 'A livelier catalyst',
    category: 'campaign',
    brief:
      'A fresh catalyst charge went in overnight and it is 70 % more active than the old one. ' +
      'Same feed, same flows — considerably more heat. Find a set of conditions the unit can ' +
      'hold, and hold them.',
    durationS: 4 * H,
    events: [
      { id: 'cat', at: 15 * 60, effects: [{ do: 'fault', id: 'CATALYST-UP', on: true }], announce: 'Laboratory report: the new catalyst charge is well above specification for activity.' },
    ],
    objectives: [
      { id: 'temp', text: 'Hold the reactor between 340 K and 358 K', kind: 'maintain', condition: { op: 'between', tag: 'TT-201.PV', lo: 340, hi: 358 }, toleranceS: 300, weight: 4 },
      { id: 'notrip', text: 'No trip', kind: 'avoid', condition: { op: 'tripped', id: 'I-201' }, weight: 4 },
      { id: 'rate', text: 'Keep at least 5 m³/h of feed A on line', kind: 'maintain', condition: { op: '>', tag: 'FT-101.PV', value: 5 }, toleranceS: 900, weight: 2 },
    ],
    stars: [50, 74, 90],
    tip: 'A more active catalyst reaches the same conversion at a lower temperature. Lowering the temperature setpoint may cost you nothing at all.',
  },

  {
    id: 'cold-start',
    name: 'Cold start',
    category: 'campaign',
    brief:
      'The reactor is cold, empty and in manual. Fill it, warm it, light the reaction off and ' +
      'settle it at 350 K — without overshooting. Once the reaction starts it makes its own ' +
      'heat, and the jacket is a long way behind. This is the hardest thing on the unit.',
    durationS: 4 * H,
    initial: 'cold',
    events: [],
    objectives: [
      { id: 'level', text: 'Establish reactor level above 70 %', kind: 'achieve', condition: { op: '>', tag: 'LT-203.PV', value: 70 }, weight: 2 },
      { id: 'temp', text: 'Reach 348 K or above', kind: 'achieve', condition: { op: '>', tag: 'TT-201.PV', value: 348 }, weight: 3 },
      { id: 'noover', text: 'Never exceed 365 K', kind: 'avoid', condition: { op: '>', tag: 'TT-201.PV', value: 365 }, weight: 4 },
      { id: 'auto', text: 'Finish with TIC-201 in AUTO and FIC-202 in CAS', kind: 'achieve', condition: { op: 'and', terms: [{ op: 'mode', loop: 'TIC-201', is: 'AUTO' }, { op: 'mode', loop: 'FIC-202', is: 'CAS' }] }, weight: 3 },
      { id: 'conv', text: 'Reach 90 % conversion', kind: 'achieve', condition: { op: '>', tag: 'R-201.CONV', value: 90 }, weight: 3 },
    ],
    stars: [45, 70, 90],
    tip: 'Warm the reactor with the preheater, not with the reaction. Bring the feed on slowly and let the jacket get ahead of the exotherm.',
  },

  {
    id: 'shutdown',
    name: 'Controlled shutdown',
    category: 'campaign',
    brief:
      'The unit comes down for a planned maintenance window. Cut the feed, take the heat out, ' +
      'empty the reactor and stop the agitator — in that order, and without lifting a relief ' +
      'valve or leaving unreacted feed in the vessel.',
    durationS: 2 * H,
    events: [],
    objectives: [
      { id: 'cool', text: 'Get the reactor below 320 K', kind: 'achieve', condition: { op: '<', tag: 'TT-201.PV', value: 320 }, weight: 3 },
      { id: 'empty', text: 'Empty the reactor below 12 % level', kind: 'achieve', condition: { op: '<', tag: 'LT-203.PV', value: 12 }, weight: 3 },
      { id: 'norelief', text: 'No relief valve lift', kind: 'avoid', condition: { op: '>', tag: 'PT-301.PV', value: 3.2 }, weight: 3 },
      { id: 'agitator', text: 'Stop the agitator last', kind: 'achieve', condition: { op: 'and', terms: [{ op: '<', tag: 'AG-201.RUN', value: 0.5 }, { op: '<', tag: 'LT-203.PV', value: 15 }] }, weight: 2 },
    ],
    stars: [50, 75, 92],
    tip: 'The sequence in the sandbox panel does this properly. Watch what it does, then do it yourself.',
  },
];

/** Tuning challenge on the reactor temperature cascade. */
export const tuningChallenges: ScenarioConfig[] = [
  {
    id: 'tune-tic201',
    name: 'Tuning challenge — reactor temperature',
    category: 'tuning',
    challengeLoop: 'TIC-201',
    challengeTarget: 1500,
    brief:
      'Two lags in series, a small process gain and an exotherm underneath that grows with ' +
      'temperature. Two setpoint changes and a cooling-water upset over ninety minutes, scored ' +
      'on the integrated absolute error of TIC-201. The inner flow loop is yours to tune too.',
    durationS: 90 * 60,
    events: [
      { id: 'sp1', at: 8 * 60, effects: [{ do: 'setSp', loop: 'TIC-201', value: 355 }] },
      { id: 'cw', at: 30 * 60, effects: [{ do: 'fault', id: 'CW-SUMMER', on: true }], announce: 'Cooling water warming.' },
      { id: 'sp2', at: 55 * 60, effects: [{ do: 'setSp', loop: 'TIC-201', value: 348 }] },
      { id: 'cwoff', at: 70 * 60, effects: [{ do: 'fault', id: 'CW-SUMMER', on: false }] },
    ],
    objectives: [
      { id: 'iae', text: 'Keep the integrated absolute error below 1500 %·s', kind: 'maintain', condition: { op: '<', tag: 'TIC-201.IAE', value: 1500 }, toleranceS: 0, weight: 5 },
      { id: 'notrip', text: 'No trip', kind: 'avoid', condition: { op: 'tripped', id: 'I-201' }, weight: 4 },
      { id: 'travel', text: 'Do not chatter the cooling-water valve', kind: 'avoid', condition: { op: 'rate', tag: 'FV-202.POS', overS: 10, gt: 12 }, weight: 2 },
    ],
    stars: [50, 74, 92],
    tip: 'Two lags in series is the case derivative was invented for — but the thermocouple noise sets a ceiling on how much of it you can use.',
  },
];
